/**
 * The People screens' refusals as sentences: the team, staff and people
 * admin routes, the profile editors and the photo door. The body's code
 * picks the sentence; a code not listed here falls to the house save
 * sentence by status. Refusal copy is authored here, except the two
 * `people` codes in errorCopy.ts's SERVER_SENTENCE_CODES, whose server
 * sentence is shown: it carries the count of clients still assigned, which
 * only the server knows.
 */

import { ApiError } from './api';
import { contactSentence, errorCodeOf, pageWriteText, serverSentence, type SupportContact } from './errorCopy';

/** One sentence per refusal code. `{contact_admin}` is the admin contact;
 *  `{max}` is the body's `max`, the length limit the route enforces. */
export const PEOPLE_REFUSALS: Readonly<Record<string, string>> = {
  last_owner_locked: "You can't remove the only active owner. Make someone else an owner first.",
  cannot_remove_self: 'You cannot remove your own login. Ask another owner.',
  member_linked:
    'This login belongs to a person on the roster. Unlink it first, or use Remove from list on that person.',
  member_referenced:
    "This login is on notes or documents, so it can't be removed. Deactivate it instead.",
  already_linked: 'That login is already linked to someone on the roster. Pick another login.',
  self_link_needs_all_clients: "You can't link your own login. Ask the office to link it for you.",
  user_not_in_tenant: "That login isn't part of this organization. Pick another login.",
  staff_not_found: 'That person is no longer on the roster. Reload the page to see the current list.',
  member_not_found: "That login isn't here any more. Refresh the page to see the current list.",
  pending_not_found: "That login isn't waiting any more. Refresh the page to see the current list.",
  email_required_for_login: 'Enter their email to give them a login.',
  email_already_member: 'That email can already sign in. Find the person in the list and change their access there.',
  email_already_invited: 'That email already has a login waiting. Find them under the waiting logins instead.',
  name_taken_active: 'Someone with that name is already on the roster. Open their record, or add a middle initial to tell them apart.',
  unit_not_found: "That unit isn't available any more. Pick another.",
  company_not_found: "That company isn't available. Pick another.",
  company_override_forbidden: "Only an owner can choose a new person's company. Leave it as it is, or ask an owner to add them.",
  private_contact_forbidden: 'Only the office can add a home address or personal phone. Leave those boxes empty, or ask the office to add them.',
  client_company_unconfigured: "Adding people isn't set up yet. Ask {contact_admin} to finish the setup.",
  invalid_row: 'One of the rows is missing an email or a role. Go back, fix that row, and try again.',
  invalid_role:
    "One of the rows has a role that can't be given from this screen. Go back and pick that row's role from the list offered.",
  preferred_name_too_long: 'Keep the preferred name to {max} characters or fewer.',
  credentials_too_long: 'Keep the credentials to {max} characters or fewer.',
  bio_too_long: 'Keep the bio to {max} characters or fewer.',
  phone_too_long: 'Keep the phone number to {max} characters or fewer.',
  invalid_billable_role: 'Choose a role from the list.',
  invalid_access_role: 'Choose an access level from the list.',
  invalid_email: 'Enter their full email address, with the @.',
  photo_not_found: "That photo isn't there any more. Refresh the page.",
};

/** The body's length limit, when it carries a whole number. */
function maxOf(e: unknown): number | null {
  if (!(e instanceof ApiError) || !e.body || typeof e.body !== 'object') return null;
  const max = (e.body as { max?: unknown }).max;
  return typeof max === 'number' && Number.isInteger(max) && max > 0 ? max : null;
}

/** A People refusal as one sentence: by code first, then the house save
 *  sentence by status. */
export function peopleRefusalText(e: unknown, contact: SupportContact): string {
  const fromServer = serverSentence(e, 'people');
  if (fromServer !== null) return fromServer;
  const code = errorCodeOf(e);
  const sentence = code === null ? undefined : PEOPLE_REFUSALS[code];
  if (sentence === undefined) return pageWriteText(e, contact);
  let text = contactSentence(sentence, contact);
  if (text.includes('{max}')) {
    const max = maxOf(e);
    if (max === null) return pageWriteText(e, contact);
    text = text.replace(/\{max\}/g, String(max));
  }
  return text;
}
